import { CheckBoxWithLabelWidget, InlineAlertType, InlineAlertWidget } from '@tableau/widgets';
import { useFormikContext } from 'formik';
import React from 'react';

import { useOnSettingsRevert } from '~settings-coordinator/SettingsCoordinatorHook';
import { CompositeSessionInfoSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { Messages } from '~src/codegen/Localize';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { SettingsSection } from '~src/settings/components/common/SettingsSection';

import { componentRegistryEntry } from '../../general-settings/components/SettingsComponentRegistryFunctions';
import { SiteSettingsForSiteAdminProps } from '../../general-settings/components/site-settings/ReactSiteSettingsForSiteAdmin';
import { useSelector } from '../../general-settings/utils';
import { EXTENSION_PACKAGE_REST_ALLOWLIST_PATH_PREFIX, ExtensionPackageRestAllowlistListView } from './components/ExtensionPackageRestAllowlistListView';
import {
  EXTENSION_PACKAGE_REST_ALLOWLIST_BYPASS_ENTRY,
  ExtensionPackageRestAllowlistCodec,
  ExtensionPackageRestAllowlistEntry,
  ExtensionPackageRestAllowlistListViolation,
} from './utils/ExtensionPackageRestAllowlistCodec';

export const EXTENSION_PACKAGE_REST_ALLOWLIST_FIELD = 'settings.siteSettingsV2.extensionPackageRestAllowlist';
// Top-level Formik error key that drives the page-level Save gate. It is deliberately NOT the nested
// EXTENSION_PACKAGE_REST_ALLOWLIST_FIELD path: Formik's setIn(errors, deepPath, undefined) deletes only the
// leaf and leaves empty ancestor objects behind, so `isValid` would never recover to true. A single
// top-level key is fully removed on clear, restoring `isValid`. This error isn't rendered anywhere
// (per-row messages come from the list view); it only signals the Save gate.
export const EXTENSION_PACKAGE_REST_ALLOWLIST_ERROR_KEY = 'extensionPackageRestAllowlistInvalid';
export const EXTENSION_PACKAGE_REST_ALLOWLIST_SECTION_TEST_ID = 'extension-package-rest-allowlist-section';
export const EXTENSION_PACKAGE_REST_ALLOWLIST_ALLOW_ALL_TEST_ID = 'extension-package-rest-allowlist-allow-all';
export const EXTENSION_PACKAGE_REST_ALLOWLIST_LIST_ERROR_TEST_ID = 'extension-package-rest-allowlist-list-error';
// Passed in as a message parameter rather than written into the string: curly braces are placeholder
// syntax in localized messages, and the path is not translatable.
const DESCRIPTION_EXAMPLE = `${EXTENSION_PACKAGE_REST_ALLOWLIST_PATH_PREFIX}workbooks/{workbook-id}`;

// Registration key for useOnSettingsRevert -- distinct from the page form's own name so it doesn't
// collide with the shared Formik registration.
const EXTENSION_PACKAGE_REST_ALLOWLIST_REVERT_ID = 'ExtensionPackageRestAllowlistSection';

export type ExtensionPackageRestAllowlistSectionProps = SiteSettingsForSiteAdminProps<'siteSettingsV2'>;

/**
 * Client-side editor for the `extensionPackageRestAllowlist` site setting: an add/remove list of
 * {url-path, verb} entries that gate the REST calls an extension package may make through fetch(), plus an
 * "allow all" checkbox for the server's full-bypass value. Integrated into the shared siteSettingsV2
 * Formik form (see DefaultSiteLanguageSection / AllowedExtensionOriginsSection for the same pattern)
 * so the page-level Save button persists it -- there is no section-local save. Rendered on the
 * Packages tab next to AllowedExtensionOrigins (see PackagesSettings).
 *
 * "Allow all" maps to the server's bypass value [{"*","*"}]: when checked, the path list is hidden
 * and only the bypass entry is persisted; the entered rows are kept in local state so unchecking
 * restores them. The `*` verb/path is intentionally not offered in the list itself -- only the exact
 * bypass pair is legal, so it lives behind this checkbox rather than the per-row dropdown.
 *
 * Only entries that pass client-side validation are committed to the stored value. A non-empty
 * invalid row -- or a list that exceeds the server size limits (see findListViolation) -- blocks the
 * page-level Save (via a Formik field error) so it can't be silently lost; blank in-progress rows
 * are allowed and simply dropped from what gets saved. The server-side ExtensionPackageRestAllowlistValidator
 * is the final authority on any value that is persisted.
 */
export function ExtensionPackageRestAllowlistSection(): React.ReactElement | null {
  const formik = useFormikContext<ExtensionPackageRestAllowlistSectionProps>();
  const showSection = useShowSection();

  const stored: string = formik.values.settings?.siteSettingsV2?.extensionPackageRestAllowlist ?? '';
  const initial: string = formik.initialValues.settings?.siteSettingsV2?.extensionPackageRestAllowlist ?? '';

  // pendingEntries + allowAll are the source of truth for rendering (pendingEntries can hold
  // blank/invalid in-progress rows the canonical stored JSON can't represent; allowAll captures the
  // bypass value the list can't). They're pushed one-way into formik on every edit; lastPushedRef
  // lets the effect below tell "formik changed because we just wrote it" apart from "formik changed
  // for some other reason" (initial load, or a page-level Cancel/reset) and only resync from formik
  // in the latter case. Mirrors AllowedExtensionOriginsSection.
  const lastPushedRef = React.useRef<string>(stored);
  const [pendingEntries, setPendingEntries] = React.useState<ExtensionPackageRestAllowlistEntry[]>(
    () => concreteEntries(ExtensionPackageRestAllowlistCodec.parse(stored))
  );
  const [allowAll, setAllowAll] = React.useState<boolean>(
    () => ExtensionPackageRestAllowlistCodec.isBypassList(ExtensionPackageRestAllowlistCodec.parse(stored))
  );

  const resyncFrom = React.useCallback((value: string) => {
    const parsed = ExtensionPackageRestAllowlistCodec.parse(value);
    setAllowAll(ExtensionPackageRestAllowlistCodec.isBypassList(parsed));
    setPendingEntries(concreteEntries(parsed));
  }, []);

  React.useEffect(() => {
    if (stored !== lastPushedRef.current) {
      lastPushedRef.current = stored;
      resyncFrom(stored);
    }
  }, [stored, resyncFrom]);

  // The [stored] effect above misses a page-level Revert when the value doesn't actually change --
  // e.g. the only in-progress edit was an invalid/blank row, which is dropped from the canonical
  // value, so `stored === lastPushedRef.current` and no resync fires. Re-derive from initial and
  // clear the field error explicitly on revert so a stale invalid row can't linger on a pristine form.
  useOnSettingsRevert(EXTENSION_PACKAGE_REST_ALLOWLIST_REVERT_ID, () => {
    lastPushedRef.current = initial;
    resyncFrom(initial);
    formik.setFieldError(EXTENSION_PACKAGE_REST_ALLOWLIST_ERROR_KEY, undefined);
  });

  if (!showSection) {
    return null;
  }

  // Push the current (allowAll, entries) state into formik and drive the page-level Save gate. The
  // shared SettingsCoordinator disables Save when the form's Formik `isValid` is false; the top-level
  // EXTENSION_PACKAGE_REST_ALLOWLIST_ERROR_KEY error drives that, and clearing it (setFieldError with
  // `undefined`) fully removes the key so `isValid` recovers once the list is valid again. (A page
  // Revert/Save reset clears it too.)
  const commit = (allowAllNext: boolean, entries: ExtensionPackageRestAllowlistEntry[]) => {
    setAllowAll(allowAllNext);
    setPendingEntries(entries);
    const validEntries = entries.filter(ExtensionPackageRestAllowlistCodec.isValidEntry);
    const canonical = allowAllNext
      ? ExtensionPackageRestAllowlistCodec.serialize([EXTENSION_PACKAGE_REST_ALLOWLIST_BYPASS_ENTRY])
      : ExtensionPackageRestAllowlistCodec.serialize(validEntries);
    lastPushedRef.current = canonical;
    // shouldValidate=false: the shared site-settings form has no validator, so a validate pass would
    // resolve to empty errors and wipe the setFieldError below (clearing the Save gate). Suppress it
    // so the top-level error we set drives isValid -> the coordinator's isAnyInvalid -> Save disabled.
    formik.setFieldValue(EXTENSION_PACKAGE_REST_ALLOWLIST_FIELD, canonical, false);
    const hasInvalidRow = !allowAllNext && entries.some(
      entry => entry.url.trim() !== '' && ExtensionPackageRestAllowlistCodec.findEntryViolation(entry) !== undefined
    );
    const listViolation = allowAllNext ? undefined : ExtensionPackageRestAllowlistCodec.findListViolation(validEntries);
    formik.setFieldError(EXTENSION_PACKAGE_REST_ALLOWLIST_ERROR_KEY, (hasInvalidRow || listViolation !== undefined) ? 'invalid' : undefined);
  };

  const listViolation = allowAll
    ? undefined
    : ExtensionPackageRestAllowlistCodec.findListViolation(pendingEntries.filter(ExtensionPackageRestAllowlistCodec.isValidEntry));

  return (
    <SettingsSection
      title={Messages.extensionPackageRestAllowlistSectionTitle()}
      description={Messages.extensionPackageRestAllowlistSectionDescription({ EXAMPLE: DESCRIPTION_EXAMPLE })}
      testId={EXTENSION_PACKAGE_REST_ALLOWLIST_SECTION_TEST_ID}>
      <CheckBoxWithLabelWidget
        label={Messages.extensionPackageRestAllowlistAllowAllLabel()}
        checked={allowAll}
        handleChange={checked => commit(checked, pendingEntries)}
        testId={EXTENSION_PACKAGE_REST_ALLOWLIST_ALLOW_ALL_TEST_ID}
      />
      {!allowAll && (
        <ExtensionPackageRestAllowlistListView entries={pendingEntries} onChange={entries => commit(false, entries)} />
      )}
      {listViolation !== undefined && (
        <InlineAlertWidget
          type={InlineAlertType.Error}
          testId={EXTENSION_PACKAGE_REST_ALLOWLIST_LIST_ERROR_TEST_ID}
          children={messageForListViolation(listViolation)}
        />
      )}
    </SettingsSection>
  );
}

/** Entries the list view owns -- everything except the bypass pair, which the checkbox owns. */
function concreteEntries(entries: readonly ExtensionPackageRestAllowlistEntry[]): ExtensionPackageRestAllowlistEntry[] {
  return entries.filter(entry => !ExtensionPackageRestAllowlistCodec.isBypassEntry(entry));
}

function messageForListViolation(violation: ExtensionPackageRestAllowlistListViolation): string {
  return violation === 'tooManyEntries'
    ? Messages.extensionPackageRestAllowlistTooManyEntriesMessage()
    : Messages.extensionPackageRestAllowlistValueTooLongMessage();
}

function useShowSection(): boolean {
  const isSiteOrServerAdmin = !!useSelector(CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector);
  return FeatureFlags.packagesEnabled && isSiteOrServerAdmin;
}

export const ExtensionPackageRestAllowlistSectionSpecs = componentRegistryEntry({
  name: 'ExtensionPackageRestAllowlistSection',
  component: ExtensionPackageRestAllowlistSection
});
